import { formatElapsedDuration } from "@/lib/time";
import { differenceInSeconds, parseISO } from "date-fns";
import { useEffect, useState } from "react";

/**
 * Live elapsed-time label ("M:SS" / "H:MM:SS") for the current provider's
 * active charting session, or null when the timer should not be shown.
 *
 * Returns null when `isActive` is false/absent (encounter released, handed-off,
 * paused, or closed) or `sessionStart` is null/absent. Re-renders once a second
 * while active; the label is recomputed from the clock each render.
 *
 * @param sessionStart ISO-8601 UTC string from the active-session endpoint.
 * @param isActive Whether a provider is actively charting this encounter.
 */
export const useVisitTimer = (
  sessionStart?: string | null,
  isActive?: boolean,
): string | null => {
  const activeStart = isActive && sessionStart ? sessionStart : null;
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!activeStart) return;
    const intervalId = setInterval(() => setTick((t) => t + 1), 1_000);
    return () => clearInterval(intervalId);
  }, [activeStart]);

  if (!activeStart) return null;

  return formatElapsedDuration(
    differenceInSeconds(new Date(), parseISO(activeStart)),
  );
};
